import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  PATH_PLANNING_ALL_STEPS,
  type PathPlanningStep,
  type PathPlanningStepStatus,
  type PathPlanningWorkflowStatus,
} from "@/lib/schemas/path-planning.schemas";

type PathPlanningStepStatuses = Record<PathPlanningStep, PathPlanningStepStatus>;

type PathPlanningStepResults = Partial<Record<PathPlanningStep, unknown>>;

type PathPlanningStepGlbUrls = Partial<Record<PathPlanningStep, string | null>>;

type PathPlanningStepErrors = Partial<Record<PathPlanningStep, string | null>>;

function createDefaultStepStatuses(): PathPlanningStepStatuses {
  return PATH_PLANNING_ALL_STEPS.reduce(
    (acc, step) => ({ ...acc, [step]: "idle" as const }),
    {} as PathPlanningStepStatuses
  );
}

const usePathPlanningWorkflowStore = create<{
  workflowId: string | null;
  runId: string | null;
  error: string | null;
  workflowStatus: PathPlanningWorkflowStatus;
  publishFileName: string | null;
  initiatedBy: string | null;
  // Browser-clock anchor for the elapsed timer. Lives here rather than in the form so it
  // survives a remount and is available to a client that did not start the run (SW-1737).
  runStartedAt: number | null;
  stepStatuses: PathPlanningStepStatuses;
  stepResults: PathPlanningStepResults;
  stepGlbUrls: PathPlanningStepGlbUrls;
  stepErrors: PathPlanningStepErrors;
  visualizedStep: PathPlanningStep | null;
  breakpoints: PathPlanningStep[];
  // Couples this singleton store to an operation so a stale run from a previous operation can't
  // out-vote server truth in the viewer (SW-1264).
  operationId: string | null;
  setOperationId: (operationId: string | null) => void;
  setWorkflowId: (workflowId: string | null) => void;
  setRunId: (runId: string | null) => void;
  setError: (error: string | null) => void;
  setWorkflowStatus: (status: PathPlanningWorkflowStatus) => void;
  setPublishFileName: (publishFileName: string | null) => void;
  setInitiatedBy: (initiatedBy: string | null) => void;
  setRunStartedAt: (runStartedAt: number | null) => void;
  setStepStatus: (step: PathPlanningStep, status: PathPlanningStepStatus) => void;
  setStepResult: (step: PathPlanningStep, result: unknown) => void;
  setStepGlbUrl: (step: PathPlanningStep, url: string | null) => void;
  setStepError: (step: PathPlanningStep, error: string | null) => void;
  setVisualizedStep: (step: PathPlanningStep | null) => void;
  toggleBreakpoint: (step: PathPlanningStep) => void;
  reset: () => void;
  softReset: () => void;
}>()(
  devtools(
    (set) => ({
      workflowId: null,
      runId: null,
      error: null,
      workflowStatus: "not_started",
      publishFileName: null,
      initiatedBy: null,
      runStartedAt: null,
      stepStatuses: createDefaultStepStatuses(),
      stepResults: {},
      stepGlbUrls: {},
      stepErrors: {},
      visualizedStep: null,
      breakpoints: [],
      operationId: null,

      setOperationId: (operationId) => set({ operationId }, false, { type: "setOperationId" }),
      setWorkflowId: (workflowId) => set({ workflowId }, false, { type: "setWorkflowId" }),
      setRunId: (runId) => set({ runId }, false, { type: "setRunId" }),
      setError: (error) => set({ error }, false, { type: "setError" }),
      setWorkflowStatus: (workflowStatus) =>
        set({ workflowStatus }, false, { type: "setWorkflowStatus" }),
      setPublishFileName: (publishFileName) =>
        set({ publishFileName }, false, { type: "setPublishFileName" }),
      setInitiatedBy: (initiatedBy) => set({ initiatedBy }, false, { type: "setInitiatedBy" }),
      setRunStartedAt: (runStartedAt) =>
        set({ runStartedAt }, false, { type: "setRunStartedAt", runStartedAt }),
      setStepStatus: (step, status) =>
        set(
          (state) => ({
            stepStatuses: {
              ...state.stepStatuses,
              [step]: status,
            },
          }),
          false,
          { type: "setStepStatus", step, status }
        ),
      setStepResult: (step, result) =>
        set(
          (state) => ({
            stepResults: {
              ...state.stepResults,
              [step]: result,
            },
          }),
          false,
          { type: "setStepResult", step }
        ),
      setStepGlbUrl: (step, url) =>
        set(
          (state) => ({
            stepGlbUrls: {
              ...state.stepGlbUrls,
              [step]: url,
            },
          }),
          false,
          { type: "setStepGlbUrl", step }
        ),
      setStepError: (step, error) =>
        set(
          (state) => ({
            stepErrors: {
              ...state.stepErrors,
              [step]: error,
            },
          }),
          false,
          { type: "setStepError", step }
        ),
      setVisualizedStep: (step) =>
        set({ visualizedStep: step }, false, { type: "setVisualizedStep", step }),
      toggleBreakpoint: (step) =>
        set(
          (state) => ({
            breakpoints: state.breakpoints.includes(step)
              ? state.breakpoints.filter((s) => s !== step)
              : [...state.breakpoints, step],
          }),
          false,
          { type: "toggleBreakpoint", step }
        ),
      reset: () =>
        set(
          {
            workflowId: null,
            runId: null,
            error: null,
            workflowStatus: "not_started",
            publishFileName: null,
            initiatedBy: null,
            runStartedAt: null,
            stepStatuses: createDefaultStepStatuses(),
            stepResults: {},
            stepGlbUrls: {},
            stepErrors: {},
            visualizedStep: null,
            operationId: null,
            // breakpoints are user preferences — not cleared on reset
          },
          false,
          { type: "reset" }
        ),
      softReset: () =>
        set(
          {
            error: null,
            workflowStatus: "running",
            runStartedAt: null,
            stepStatuses: createDefaultStepStatuses(),
            stepResults: {},
            stepGlbUrls: {},
            stepErrors: {},
            visualizedStep: null,
            // breakpoints are user preferences — not cleared on softReset
          },
          false,
          { type: "softReset" }
        ),
    }),
    { name: "PathPlanningWorkflowStore" }
  )
);

export { usePathPlanningWorkflowStore };
